import _ from 'lodash'
import Guard from 'arg-guard'

import Service from './service'
import ServiceOperation from './service-operation'
import Logger from '@/logger'

import Home from '@/models/home'
import Property from '@/models/property'

/**
 * Gateway for accessing Property data.
 *
 * @export AddressService
 * @class AddressService
 */
export default class AddressService extends Service {

    /**
     * Creates an instance of AddressService.
     * @memberof AddressService
     * @param {?number} schemeId
     */
    constructor({ schemeId } = { schemeId: null }) {
        super('/address', 'Address')

        Guard.throwIf(schemeId, 'schemeId')
        this.schemeId = schemeId
    }

    /**
     * Load property data from an address ID.
     * @param {number} addressId
     * @returns {Promise<ServiceOperation>} The operation result.
     * @memberof AddressService
     */
    async loadMyHomeDataAsync(addressId) {
        Guard.throwIf(addressId, 'addressId')

        const operationState = new ServiceOperation('My Home Data Load', true)
        if (addressId === 0)
            return this._failOperation(new Error('No address ID specified (0)'), operationState, 'loadMyHomeDataAsync')

        const url = `${ addressId }/data/for-scheme/${ this.schemeId }`
        Logger.log(`${ this.baseUrl }/${ url }`, 'Request [GET]')

        try {
            return this._processData(await this.axios.get(url, this._buildAxiosConfig()), operationState, 'loadMyHomeDataAsync',
                (sourceData) => new Home(sourceData))
        } catch (err) {
            return this._failOperation(err, operationState, 'loadMyHomeDataAsync')
        }
    }

    /**
     * Search for a postcode.
     * @param {string} postcode
     * @returns {Promise<ServiceOperation>} The operation result.
     * @memberof AddressService
     */
    async searchAsync(postcode) {
        Guard.throwIf(postcode, 'postcode')

        const sort = function(addresses) {
            return _.sortBy(addresses, [ a => {
                const first = a.address.split(' ')[0]
                return parseInt(first)
            }, a => a.address.split(' ')[0] ])
        }

        const operationState = new ServiceOperation('Postcode Search', true)
        const url = `search/${ postcode }/for-scheme/${ this.schemeId }`
        Logger.log(`${ this.baseUrl }/${ url }`, 'Request [GET]')

        try {
            return this._processData(await this.axios.get(url, this._buildAxiosConfig()), operationState, 'searchAsync', data => sort(data
                .filter(item => !item.perhapsOrganisation)
                .map(item => Property.ensure(item))
            ))
        } catch (err) {
            return this._failOperation(err, operationState, 'searchAsync')
        }
    }

    /**
     * Updates segment data for the address.
     * @param {number} addressId
     * @param {string} segmentKey
     * @param {Array} changedItems An array of changed items.
     * @returns {Promise<ServiceOperation>} The operation result.
     * @memberof AddressService
     */
    async updateSegmentAsync(addressId, segmentKey, changedItems) {
        Guard.throwIf(addressId, 'addressId')
        Guard.throwIf(segmentKey, 'segmentKey')
        Guard.throwIf(changedItems, 'changedItems')

        if (addressId === 0)
            Logger.warn('Attempt to update my home data with an Address Id of 0')

        const operationState = new ServiceOperation('My Home Data Load', true)
        const url = `${ addressId }/data/for-scheme/${ this.schemeId }`
        const data = {
            items: changedItems,
        }

        Logger.logObject(data, `${ this.baseUrl }/${ url }`, 'Request [PUT]')

        try {
            return this._processResponse(await this.axios.put(url, data, this._buildAxiosConfig()), operationState, 'updateSegmentAsync')
        } catch (err) {
            return this._failOperation(err, operationState, 'updateSegmentAsync')
        }
    }
}
